import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'
const WEB_BASE_URL = import.meta.env.VITE_WEB_BASE_URL || window.location.origin
const TOKEN_KEY = 'nocal_web_auth_token'

export function getApiBaseUrl() {
  return API_BASE_URL
}

export function getWebBaseUrl() {
  return WEB_BASE_URL
}

export function getToken() {
  const sessionToken = sessionStorage.getItem(TOKEN_KEY)
  if (sessionToken) {
    return sessionToken
  }

  // One-time migration path from previous localStorage persistence.
  const legacyToken = localStorage.getItem(TOKEN_KEY)
  if (legacyToken) {
    sessionStorage.setItem(TOKEN_KEY, legacyToken)
    localStorage.removeItem(TOKEN_KEY)
    return legacyToken
  }

  return null
}

export function setToken(token: string) {
  sessionStorage.setItem(TOKEN_KEY, token)
  // Keep localStorage clear so token lifetime matches browser session.
  localStorage.removeItem(TOKEN_KEY)
}

export function clearToken() {
  sessionStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(TOKEN_KEY)
}

export const webApi = axios.create({
  baseURL: API_BASE_URL
})

webApi.interceptors.request.use((config) => {
  const token = getToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export async function lookupReferralCode(code: string) {
  const response = await webApi.get(`/referrals/code/${encodeURIComponent(code)}/`)
  return response.data
}

export async function getReferralLink() {
  const response = await webApi.get('/me/referral-link/')
  return response.data
}

export async function getMyPromos() {
  const response = await webApi.get('/promos/')
  return response.data
}

export interface ConnectedOAuthApp {
  application_id: number
  name: string
  client_id: string
  scopes: string[]
  expires_at: string | null
}

export async function getConnectedApps(): Promise<ConnectedOAuthApp[]> {
  const response = await webApi.get<ConnectedOAuthApp[]>('/me/oauth-apps/')
  return response.data
}

export async function revokeConnectedApp(applicationId: number): Promise<void> {
  await webApi.post(`/me/oauth-apps/${applicationId}/revoke/`)
}

const AUTH_BASE_URL = import.meta.env.VITE_AUTH_BASE_URL || API_BASE_URL

export function buildGoogleWebLoginUrl(referralCode?: string | null) {
  const next = `${WEB_BASE_URL}/auth/complete`
  const encodedNext = encodeURIComponent(next)
  let url = `${AUTH_BASE_URL}/auth/google/login?platform=web&next=${encodedNext}`
  if (referralCode && referralCode.trim()) {
    url += `&ref=${encodeURIComponent(referralCode.trim())}`
  }
  return url
}

export function buildMicrosoftWebLoginUrl(referralCode?: string | null) {
  const next = `${WEB_BASE_URL}/auth/complete`
  const encodedNext = encodeURIComponent(next)
  let url = `${API_BASE_URL}/auth/microsoft/login?platform=web&next=${encodedNext}`
  if (referralCode && referralCode.trim()) {
    url += `&ref=${encodeURIComponent(referralCode.trim())}`
  }
  return url
}
